import React, { useState } from "react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { IMG, BRAND } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { Mail, MapPin, MessageCircle, Send, Instagram, Youtube, Facebook, Users } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { content } = useContent();
  const b = content.brand || {};
  const email = b.email || BRAND.email;
  const city = b.city || BRAND.city;
  const number = (b.whatsappNumber || BRAND.whatsappNumber).replace(/\D/g, "");
  const msg = b.whatsappMessage || BRAND.whatsappMessage;
  const wa = `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  const community = b.whatsappCommunity || "";
  const instagram = b.instagram || BRAND.socials.instagram;
  const youtube = b.youtube || BRAND.socials.youtube;
  const facebook = b.facebook || BRAND.socials.facebook;

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setSubmitting(true);
    // Save a copy to the database for record (best-effort — never blocks WhatsApp)
    axios.post(`${API}/contact`, {
      name: form.name,
      email: form.email,
      phone: form.phone || "",
      message: form.message,
    }).catch(() => {});

    // Open WhatsApp with the enquiry pre-filled to the studio number
    const phoneLine = form.phone ? `Phone: ${form.phone}\n` : "";
    const text = `Namaste, I am ${form.name} (${form.email}).\n${phoneLine}\n${form.message}`;
    const url = `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — your message is ready to send.");
    setForm({ name: "", email: "", phone: "", message: "" });
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      <Toaster theme="dark" position="top-right" />
      <PageHero
        eyebrow="Begin Your Journey"
        title={<>A <em>conversation</em> is where it begins.</>}
        subtitle="The founder personally responds to every initial enquiry. Reach out on WhatsApp for the fastest reply."
        image={IMG.temple}
      />

      <section className="section">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="glass-strong p-8 grid gap-5" data-testid="contact-form">
              <div className="eyebrow">Send a message</div>
              <h2 className="font-display text-3xl text-ivory">Tell us what you seek.</h2>

              <div className="grid gap-2">
                <label className="text-xs uppercase tracking-[0.25em] text-ivory/55">Your Name</label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-midnight/60 border border-gold/20 px-4 py-3 text-ivory focus:outline-none focus:border-gold transition"
                  placeholder="Your full name"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-xs uppercase tracking-[0.25em] text-ivory/55">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-midnight/60 border border-gold/20 px-4 py-3 text-ivory focus:outline-none focus:border-gold transition"
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-xs uppercase tracking-[0.25em] text-ivory/55">Phone (optional)</label>
                <div className="bg-midnight/60 border border-gold/20 px-4 py-3 focus-within:border-gold transition">
                  <PhoneInput
                    data-testid="contact-input-phone"
                    international
                    defaultCountry="IN"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v || "" })}
                    className="rpi"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <label className="text-xs uppercase tracking-[0.25em] text-ivory/55">Your Message</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-midnight/60 border border-gold/20 px-4 py-3 text-ivory focus:outline-none focus:border-gold transition resize-none"
                  placeholder="Share the desire or question that brings you here."
                />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold mt-3 justify-center disabled:opacity-50" data-testid="contact-submit">
                {submitting ? "Opening WhatsApp…" : "Send via WhatsApp"} <Send className="h-4 w-4" />
              </button>
              <p className="text-xs text-ivory/45 leading-relaxed">
                Your message will open in WhatsApp pre-filled to the studio. Tap Send in WhatsApp to deliver it.
              </p>
            </form>
          </Reveal>

          <Reveal delay={150} className="lg:col-span-5">
            <div className="glass p-8">
              <div className="eyebrow mb-3">WhatsApp</div>
              <h3 className="font-display text-2xl text-ivory">Chat directly</h3>
              <p className="mt-2 text-ivory/65 text-sm">
                We respond within a few hours, every day.
              </p>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="btn-gold mt-5 w-full justify-center"
                data-testid="contact-whatsapp-cta"
              >
                <MessageCircle className="h-4 w-4" /> Open WhatsApp
              </a>
              {community && (
                <a
                  href={community}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost mt-3 w-full justify-center"
                  data-testid="contact-community-cta"
                >
                  <Users className="h-4 w-4" /> Join WhatsApp Community
                </a>
              )}
            </div>

            <div className="glass p-8 mt-6 grid gap-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 mt-0.5 text-gold" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-ivory/55">Email</div>
                  <a href={`mailto:${email}`} className="text-ivory hover:text-gold">
                    {email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5 text-gold" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-ivory/55">Studio</div>
                  <div className="text-ivory">{city}</div>
                </div>
              </div>
              <div className="gold-divider" />
              <div className="flex gap-3">
                {[
                  { Icon: Instagram, href: instagram, label: "instagram" },
                  { Icon: Youtube, href: youtube, label: "youtube" },
                  { Icon: Facebook, href: facebook, label: "facebook" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${label}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-midnight transition"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
